import * as Sentry from '@sentry/vue';
import type Hls from 'hls.js';
import type {
  HlsConfig,
  LoaderCallbacks,
  LoaderConfiguration,
  LoaderContext,
  LoaderResponse,
} from 'hls.js';
import { findSegmentUrls, findVariantUrls, pinHighestVariant } from './hls-playlist';

/**
 * IndexedDB cache for the HLS videos of the counter video wall. Playlists and segments are stored
 * by URL; media file keys are unique per upload, so a cached file never goes stale and only
 * expires to free up space.
 */

type CachedFile = {
  url: string;
  data: string | ArrayBuffer;
  cachedAt: number;
};

const DB_NAME = 'ros-video-wall-cache';
const STORE = 'files';
const CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const PRUNE_INTERVAL_MS = 60 * 60 * 1000;

let dbPromise: Promise<IDBDatabase> | null = null;
let hasReportedWriteFailure = false;
let lastPrunedAt = 0;
const runningPrefetches = new Map<string, Promise<void>>();

function openDb(): Promise<IDBDatabase> {
  dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const store = req.result.createObjectStore(STORE, { keyPath: 'url' });
      store.createIndex('cachedAt', 'cachedAt');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    // Without this the browser may evict the cache under storage pressure.
    void navigator.storage?.persist?.().catch(() => false);
  });
  return dbPromise;
}

function waitForIdbRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function reportWriteFailure(error: unknown) {
  if (hasReportedWriteFailure) return;
  hasReportedWriteFailure = true;
  Sentry.captureException(error, { tags: { feature: 'counter-video-wall' } });
}

async function readCachedFile(url: string): Promise<CachedFile | null> {
  try {
    const db = await openDb();
    const file = await waitForIdbRequest<CachedFile | undefined>(
      db.transaction(STORE).objectStore(STORE).get(url),
    );
    if (!file || Date.now() - file.cachedAt > CACHE_MAX_AGE_MS) return null;
    return file;
  } catch {
    return null;
  } finally {
    pruneExpiredFilesIfDue();
  }
}

async function writeCachedFile(url: string, data: string | ArrayBuffer): Promise<void> {
  const file: CachedFile = { url, data, cachedAt: Date.now() };
  try {
    const db = await openDb();
    await waitForIdbRequest(db.transaction(STORE, 'readwrite').objectStore(STORE).put(file));
  } catch (error) {
    // Playback keeps streaming from the network when the cache is full or unavailable.
    reportWriteFailure(error);
  }
}

async function pruneExpiredFiles(): Promise<void> {
  try {
    const db = await openDb();
    const expired = IDBKeyRange.upperBound(Date.now() - CACHE_MAX_AGE_MS);
    const cursorRequest = db
      .transaction(STORE, 'readwrite')
      .objectStore(STORE)
      .index('cachedAt')
      .openCursor(expired);
    await new Promise<void>((resolve, reject) => {
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (!cursor) {
          resolve();
          return;
        }
        cursor.delete();
        cursor.continue();
      };
      cursorRequest.onerror = () => reject(cursorRequest.error);
    });
  } catch {
    // Retried with the next lookup once the prune interval has passed.
  }
}

/** Expired files are never served, so pruning only frees space and runs at most once an hour. */
function pruneExpiredFilesIfDue() {
  if (Date.now() - lastPrunedAt < PRUNE_INTERVAL_MS) return;
  lastPrunedAt = Date.now();
  void pruneExpiredFiles();
}

/** Stores a response hls.js loaded from the network and returns the data to hand to hls.js. */
function cacheResponseData(url: string, data: LoaderResponse['data']): LoaderResponse['data'] {
  if (typeof data === 'string') {
    const playlist = pinHighestVariant(data);
    void writeCachedFile(url, playlist);
    return playlist;
  }
  if (data instanceof ArrayBuffer) {
    // hls.js may transfer the buffer to its worker, which detaches it before the write runs.
    void writeCachedFile(url, data.slice(0));
  }
  return data;
}

/**
 * hls.js loader that serves playlists and segments from IndexedDB and caches whatever it has to
 * load from the network. Multivariant playlists are reduced to their highest quality variant.
 */
export function createCachingLoader(HlsModule: typeof Hls): HlsConfig['loader'] {
  return class CachingLoader extends HlsModule.DefaultConfig.loader {
    private isAborted = false;
    private lookupCallbacks: LoaderCallbacks<LoaderContext> | null = null;

    load(
      context: LoaderContext,
      config: LoaderConfiguration,
      callbacks: LoaderCallbacks<LoaderContext>,
    ) {
      // Byte-range requests share their URL with other ranges, so they would collide in the cache.
      if (context.rangeEnd) {
        super.load(context, config, callbacks);
        return;
      }

      this.context = context;
      this.lookupCallbacks = callbacks;
      void readCachedFile(context.url).then((file) => {
        if (this.isAborted) return;
        this.lookupCallbacks = null;

        if (file) {
          this.deliverCachedFile(file, context, callbacks);
          return;
        }

        super.load(context, config, {
          ...callbacks,
          onSuccess: (response, stats, loadedContext, networkDetails) => {
            const data = cacheResponseData(loadedContext.url, response.data);
            callbacks.onSuccess({ ...response, data }, stats, loadedContext, networkDetails);
          },
        });
      });
    }

    abort() {
      this.isAborted = true;
      if (this.lookupCallbacks && this.context) {
        this.stats.aborted = true;
        this.lookupCallbacks.onAbort?.(this.stats, this.context, null);
        this.lookupCallbacks = null;
      }
      super.abort();
    }

    destroy() {
      this.isAborted = true;
      this.lookupCallbacks = null;
      super.destroy();
    }

    private deliverCachedFile(
      file: CachedFile,
      context: LoaderContext,
      callbacks: LoaderCallbacks<LoaderContext>,
    ) {
      const now = self.performance.now();
      const { stats } = this;
      stats.loading.start = now;
      stats.loading.first = now;
      stats.loading.end = now;
      stats.loaded = typeof file.data === 'string' ? file.data.length : file.data.byteLength;
      stats.total = stats.loaded;
      callbacks.onSuccess({ url: context.url, data: file.data, code: 200 }, stats, context, null);
    }
  };
}

async function fetchOk(url: string): Promise<Response> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: HTTP ${response.status}`);
  }
  return response;
}

async function loadPlaylist(url: string): Promise<string> {
  const cached = await readCachedFile(url);
  if (typeof cached?.data === 'string') return cached.data;

  const response = await fetchOk(url);
  const playlist = pinHighestVariant(await response.text());
  await writeCachedFile(url, playlist);
  return playlist;
}

async function cacheSegment(url: string): Promise<void> {
  if (await readCachedFile(url)) return;

  const response = await fetchOk(url);
  await writeCachedFile(url, await response.arrayBuffer());
}

async function cacheVideo(fileUrl: string): Promise<void> {
  const playlist = await loadPlaylist(fileUrl);
  const [variantUrl] = findVariantUrls(playlist, fileUrl);
  const mediaPlaylistUrl = variantUrl ?? fileUrl;
  const mediaPlaylist = variantUrl ? await loadPlaylist(variantUrl) : playlist;

  for (const segmentUrl of findSegmentUrls(mediaPlaylist, mediaPlaylistUrl)) {
    await cacheSegment(segmentUrl);
  }
}

/**
 * Downloads the highest quality variant of a video into the cache, so it starts without network
 * delay. Never rejects: a failed prefetch only means streaming from the network.
 */
export function prefetchHlsVideo(fileUrl: string): Promise<void> {
  const running = runningPrefetches.get(fileUrl);
  if (running) return running;

  const prefetch = cacheVideo(fileUrl)
    .catch(() => {
      // Playback streams whatever is missing and reports its own failures.
    })
    .finally(() => runningPrefetches.delete(fileUrl));
  runningPrefetches.set(fileUrl, prefetch);
  return prefetch;
}
